import React, { useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Linking,
  ActivityIndicator,
} from "react-native";
import lightStyles, { createHomeScreenThemeStyles } from "../HomeScreen.style";
import { TEXT } from "../../../constants/texts";
import { useSelector } from "react-redux";
import CalendarIcon from "./icons/CalendarIcon";
import { useTheme } from "../../../theme";

interface EventItem {
  id: string | number;
  eventName?: string;
  shortDescription?: string;
  eventUrl?: string;
  status?: string;
  title: string;
  messageText: string;
  category: string;
  order: number;
  link: {
    Description: string;
    Url: string;
  };
  image: {
    Description: string;
    Url: string;
  };
  createdDate: string;
  modifiedDate: string;
}

interface UpcomingEventsProps {
  upcomingEvents: EventItem[] | string[];
  setUpcomingEventsOverlayVisible: (isVisible: boolean) => void;
}

const UpcomingEvents: React.FC<UpcomingEventsProps> = ({
  upcomingEvents,
  setUpcomingEventsOverlayVisible,
}) => {
  const { colors } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  // Get loading and error states from Redux
  const { loading } = useSelector((state: any) => state.homescreen);
  const isEventsLoading = loading.events;
  // Helper function to handle URL opening with error handling
  const handleLinkPress = (url: string, description: string) => {
    if (url) {
      Linking.openURL(url).catch((err) => {
        console.error("An error occurred opening the link:", err);
      });
    } else {
      console.log(`No URL available for: ${description}`);
    }
  };

  const isEventObject = (item: EventItem | string): item is EventItem => {
    return typeof item === "object" && item !== null;
  };

  const renderEventItem = (event: EventItem | string, index: number) => {
    if (isEventObject(event)) {
      const url = event.eventUrl || event.link?.Url || "";
      const description =
        event.shortDescription || event.link?.Description || TEXT.COMMON.COMING_SOON;
      const displayText = event.eventName || event.title || event.shortDescription || "";
      const hasLink = Boolean(url);

      return (
        <View>
          <TouchableOpacity
            key={event.id || index}
            style={lightStyles.eventItem}
            onPress={() => {
              if (hasLink) {
                handleLinkPress(url, description);
              } else {
                console.log(`Event clicked: ${displayText}`);
              }
            }}
          >
            <View style={[lightStyles.eventIconContainer, themeStyles.eventIconContainer]}>
              <CalendarIcon width={16} height={16} />
            </View>
            <View style={lightStyles.eventTextContainer}>
              <Text style={[lightStyles.eventItemText, themeStyles.eventItemText]}>{displayText}</Text>
              <Text style={[lightStyles.eventDateText, themeStyles.eventDateText]}>{description}</Text>
            </View>
          </TouchableOpacity>
          <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
        </View>
      );
    } else {
      // Legacy string format
      return (
        <View>
          <TouchableOpacity
            key={index}
            style={lightStyles.eventItem}
            onPress={() => console.log(`Event clicked: ${event}`)}
          >
            <View style={[lightStyles.eventIconContainer, themeStyles.eventIconContainer]}>
              <CalendarIcon width={16} height={16} />
            </View>
            <View style={lightStyles.eventTextContainer}>
              <Text style={[lightStyles.eventItemText, themeStyles.eventItemText]}>{event}</Text>
              <Text style={[lightStyles.eventDateText, themeStyles.eventDateText]}>{TEXT.COMMON.COMING_SOON}</Text>
            </View>
          </TouchableOpacity>
          <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
        </View>
      );
    }
  };

  // Filter out expired events
  const filteredEvents = upcomingEvents
    ? upcomingEvents.filter((event) =>
        !(typeof event === "object" && event !== null && (event as EventItem).status === "Expired")
      )
    : [];

  // Check if we have data to display
  const hasData = filteredEvents.length > 0;

  // Show loading state
  if (isEventsLoading) {
    return (
      <View style={[lightStyles.bottomCard, themeStyles.cardSurface]}>
        <Text style={[lightStyles.sectionHeader, themeStyles.sectionHeader]}>{TEXT.HEADERS.UPCOMING_EVENTS}</Text>
        <View style={lightStyles.externalLoadingContainer}>
          <ActivityIndicator size="large" color={colors.brand.primary} />
          <Text style={[lightStyles.externalLoadingText, themeStyles.externalLoadingText]}>Loading events...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[lightStyles.bottomCard, themeStyles.cardSurface]}>
      <Text style={[lightStyles.sectionHeader, themeStyles.sectionHeader]}>{TEXT.HEADERS.UPCOMING_EVENTS}</Text>
      <ScrollView style={lightStyles.scrollContent}>
        {hasData ? (
          <>
            {filteredEvents.map((event: any, index: number) => (
              <React.Fragment key={typeof event === 'object' ? (event.id || index) : `event-${index}`}>
                {renderEventItem(event, index)}
              </React.Fragment>
            ))}
          </>
        ) : (
          <View style={lightStyles.noDataContainer}>
            <Text style={[lightStyles.noDataText, themeStyles.noDataText]}>{TEXT.COMMON.NO_DATA}</Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

export default UpcomingEvents;
